import type { Platform, Problem, ValidationResult } from "../lib/types";

/**
 * Per-photo verdicts, collapsed into the per-platform one `TargetPicker` draws.
 *
 * A carousel is accepted or rejected whole — Instagram refuses the post for one
 * PNG in slot four [C008] — so a platform is ready only when every photo is,
 * and the problems are the union of the photos'. Each is labelled with the
 * slide it came from, because "must be JPEG" about a post with six photos in it
 * is not something anyone can act on; identical problems from several photos
 * collapse into one line naming them all, which is the common case when
 * somebody drops six PNGs in at once.
 */
export function mergeVerdicts(
  perItem: Array<ValidationResult[]>,
  platforms: Platform[],
  label: boolean,
): Map<string, ValidationResult> {
  const merged = new Map<string, ValidationResult>();

  for (const platform of platforms) {
    const rows = perItem
      .map((results) => results.find((r) => r.platform === platform))
      .filter((r): r is ValidationResult => !!r);
    if (rows.length === 0) continue;

    // Keyed by the problem itself, valued by which photos raised it.
    const problems = new Map<string, { problem: Problem; slides: number[] }>();
    rows.forEach((row, i) => {
      for (const problem of row.problems) {
        const key = `${problem.severity}|${problem.code}|${problem.message}`;
        const seen = problems.get(key);
        if (seen) seen.slides.push(i + 1);
        else problems.set(key, { problem, slides: [i + 1] });
      }
    });

    merged.set(platform, {
      platform,
      placement: rows[0].placement,
      ok: rows.every((r) => r.ok),
      problems: [...problems.values()].map(({ problem, slides }) => ({
        ...problem,
        message: label && slides.length < perItem.length
          ? `${slides.length === 1 ? "Photo" : "Photos"} ${slides.join(", ")}: ${problem.message}`
          : problem.message,
      })),
      sourceUrl: rows.find((r) => r.sourceUrl)?.sourceUrl ?? "",
    });
  }

  return merged;
}
